import { CurrencyIsoCode } from "@/enums";
import { OrderItemV2 } from "@/models/orders/OrderDraft";
import { isFixedPriceCountry } from "@/libs/global-e/countries";

// GE `OnCheckoutStepLoaded`(CONFIRMATION) payload → 분석 발화 입력. **순수 함수**(발화는 호출부).
//
// ## 이 파일이 존재하는 이유 — 초판(#5789)이 틀린 자리다
//
// 초판은 콜백이 `OrderID`/`StepId` 정도만 준다고 보고 서버 라우트에서 `GetOrdersDetails` 를
// 다시 조회했다. **dev sandbox 실측(2026-09-17)에서 전제가 셋 다 깨졌다**(#5778):
//
// 1. 필드명은 `OrderID` 가 아니라 **`OrderId`** — 초판은 `undefined` 를 읽어 **발화 0건**이었다
// 2. `MerchantOrderId` 는 **null** 이다. 서버 조회(`GetOrdersDetails`)값은 2026-09-17 관측에서 draft id 였지만
//    그땐 `OrderId`·`InternalOrderId` 가 같은 UUID 라 어느 쪽인지 모른다. PerformOrderPayment 에코는 우리
//    `InternalOrderId` 다(`perform-order.ts` 머리 주석, 2026-09-28 실측) — 조회값도 같은지는 미실측
// 3. **`details` 에 통화·금액·아이템이 전부 있다**(PII 없음) → 서버 조회 자체가 불필요
//
// 게다가 타이밍이 막는다: CONFIRMATION 시점 기준 draft 는 **T+7s**, 주문번호는 **T+16s** 에
// 생긴다(실측). 발화 순간 우리 쪽 식별자는 **존재하지 않는다** → `transaction_id` 는 GE `OrderId`
// 를 쓴다(GE 공식 샘플도 `transaction_id: data.OrderId`).
//
// ## 금액 그룹 — GE 는 세 벌로 준다. 고르는 규칙이 있다
//
// | 그룹                                  | 뜻                                  |
// | ------------------------------------- | ----------------------------------- |
// | `MerchantTransaction`                 | 우리가 GE 에 청구(정산) — draft·어드민 |
// | `CustomerTransaction`                 | 고객 실결제 (고객 통화)               |
// | `CustomerTransactionInMerchantCurrency` | 고객 실결제를 merchant 통화로        |
//
// **고객 결제액을, 우리 퍼널 통화로 표현한 값**을 쓴다 — `begin_checkout` 과 통화가 갈리지 않게.
// GE 는 두 표현을 다 주므로 국가 유형으로 고른다:
//
// - **Fixed**(US·GB·유로존): 우리 퍼널 통화 = 고객 통화 → `CustomerTransaction`
// - **Dynamic**(AU·CA·JP…): 우리 퍼널 통화 = USD = merchant 통화 → `…InMerchantCurrency`
//
// ⚠️ **`MerchantTransaction` 을 분석에 쓰지 말 것.** `EstimatedTotalPrice` 가 **음수로 온다**
// (실측 −2.85 — DDP 보조금 할인이 섞인다). GE 공식 샘플이 그 필드를 `value` 로 쓰는데,
// 그대로 따르면 GA4 에 음수 매출이 들어간다.
//
// ⚠️ 그룹 이름이 길고 비슷해서 **틀린 그룹을 읽어도 숫자가 그럴듯하다**(11.07 vs 11.34).
// 가드가 세 그룹에 서로 다른 값을 넣은 실측 픽스처로 잠근다.

// ## 알려진 한계 — 게임 키를 구별할 수 없다
//
// `ProductInformation[]` 에 `IsVirtual` 같은 플래그가 **없다**(실측 필드: SKU · ProductName ·
// ProductGroupCode · Quantity · CartItemId · ParentCartItemID · ProductPrices …). 서버 payload
// (`SendOrderToMerchant`)에는 `IsVirtual` 이 있어서 draft 는 게임을 가르지만, 클라 콜백에서는
// 못 가른다. 그래서 게임 키도 일반 아이템으로 나간다 — **금액은 맞고 분류만 다르다**
// (Meta content 메타데이터 한정). 게임 포함 GE 주문으로 확인 후 필요하면 보완한다.

/** GE `ProductInformation[]` 한 줄 (우리가 읽는 부분만). */
interface GeProductPriceSet {
  CustomerListPrice?: number;
  CustomerSalePrice?: number;
  CustomerDiscountedPrice?: number;
  CustomerTotalPrice?: number;
  CustomerListPriceInMerchantCurrency?: number;
  CustomerSalePriceInMerchantCurrency?: number;
  CustomerDiscountedPriceInMerchantCurrency?: number;
  CustomerTotalPriceInMerchantCurrency?: number;
}

export interface GeProductInformation {
  SKU?: string;
  ProductName?: string;
  /** 우리 slug 와 같은 값이 온다(실측: `original-badge-for-tactsuit-air`). */
  ProductGroupCode?: string;
  /** ⚠️ 문자열로 온다(실측 `"1"`). */
  Quantity?: string | number;
  /**
   * 번들 구성품이면 부모 번들 라인의 CartItemId. 최상위/비번들이면 `null`.
   *
   * ⚠️ **클라 콜백은 `ParentCartItemID`(대문자 ID), 서버 payload 는 `ParentCartItemId`(Id)** 다.
   * 두 API 의 표기가 다르다 — `OrderID`/`OrderId` 와 같은 함정이고 방향만 반대다.
   * 여기서는 **실측으로 확인된 클라 표기**를 쓰되, 서버 표기도 같이 읽어 둔다.
   */
  ParentCartItemID?: string | null;
  ParentCartItemId?: string | null;
  ProductPrices?: {
    CustomerTransaction?: GeProductPriceSet;
    CustomerTransactionInMerchantCurrency?: GeProductPriceSet;
  };
}

interface GeOrderPriceSet {
  CustomerTotalProductsPrice?: number;
  CustomerShippingPrice?: number;
  CustomerTotalProductsPriceInMerchantCurrency?: number;
  CustomerShippingPriceInMerchantCurrency?: number;
}

/** GE CONFIRMATION `details` (우리가 읽는 부분만). */
export interface GeCheckoutDetails {
  CustomerCurrencyCode?: string;
  MerchantCurrencyCode?: string;
  /** 고객 배송 국가. 금액 그룹 선택 축. */
  CountryCode?: string;
  OrderPrices?: {
    CustomerTransaction?: GeOrderPriceSet;
    CustomerTransactionInMerchantCurrency?: GeOrderPriceSet;
  };
  ProductInformation?: GeProductInformation[];
}

/** Meta `Purchase` · Google Ads 전환에 필요한 것만. GA4 는 mgmt 서버사이드 MP 소관(#5778). */
export interface GePurchasePayload {
  /** `transaction_id` / Meta `event_id`. GE OrderId — 발화 시점에 얻을 수 있는 유일한 식별자. */
  orderId: string;
  items: OrderItemV2[];
  currency: string;
  /**
   * 고객이 **제품에 지불한 총액(순가 합)** — GE `CustomerTotalProductsPrice`
   * (*"The total price paid by the customer for the products (only)"*).
   *
   * ⚠️ 자체 결제의 `order.subtotalAmount` 와 **같은 축이 아니다** — 그쪽은
   * `priceSummary.totalOriginalPrice` = **정가 합**이고 `discountAmount` 가 따로 붙는다
   * (`useCheckout.ts:317` · `apply-coupon.ts:200`). 이벤트 레벨 `value` 규약
   * (`docs/analytics-funnel-policy.md` §1.2: `(price − discount) × qty`)에는 **이쪽이 맞고**,
   * 자체 결제 경로가 정가를 보내는 pre-existing 어긋남이다. 그래서 **결제수단 간 Meta `value`
   * 를 직접 비교하면 안 된다.**
   */
  subtotalAmount: number;
  shippingPrice: number;
  shippingCountry: string;
}

/** `payload:null` 이어도 **왜 비었는지**는 남긴다 — 드롭 사유가 유실되면 진단이 안 된다. */
export interface GePurchasePayloadResult {
  payload: GePurchasePayload | null;
  warnings: string[];
}

const round2 = (n: number) => Math.round(n * 100) / 100;
const num = (v: unknown): number =>
  typeof v === "number" && Number.isFinite(v) ? v : 0;

/** `Quantity` 는 문자열로 온다(실측 `"1"`). 0·결측은 1로 접는다. */
function toQty(v: unknown): number {
  const n = typeof v === "string" ? parseInt(v, 10) : Number(v);
  return Number.isInteger(n) && n > 0 ? n : 1;
}

export function buildGePurchasePayload(
  orderId: string,
  details: GeCheckoutDetails | undefined,
): GePurchasePayloadResult {
  // ★두 사유를 가른다 — **초판(#5789)이 실제로 죽은 자리**다. `OrderID` 오타로 `orderId` 가
  // 빈 문자열이 돼 여기서 빠졌고 그게 발화 0건의 원인이었는데, 호출부 로그 한 줄로는
  // 「식별자가 없다」와 「payload 가 없다」가 구별되지 않았다.
  if (!orderId) {
    return {
      payload: null,
      warnings: ["missing GE OrderId in callback payload — not fired"],
    };
  }
  if (!details) {
    return {
      payload: null,
      warnings: ["missing `details` in callback payload — not fired"],
    };
  }

  // 금액 그룹 선택 — 퍼널 통화와 일치하는 표현을 고른다(헤더 주석).
  const fixed = isFixedPriceCountry(details.CountryCode);
  const currency = (
    (fixed ? details.CustomerCurrencyCode : details.MerchantCurrencyCode) || ""
  )
    .trim()
    .toUpperCase();
  if (!/^[A-Z]{3}$/.test(currency)) {
    return {
      payload: null,
      warnings: [`invalid currency code "${currency}" — not fired`],
    };
  }

  const warnings: string[] = [];
  const orderPrices = fixed
    ? details.OrderPrices?.CustomerTransaction
    : details.OrderPrices?.CustomerTransactionInMerchantCurrency;

  const items: OrderItemV2[] = [];
  for (const line of details.ProductInformation ?? []) {
    const prices = fixed
      ? line.ProductPrices?.CustomerTransaction
      : line.ProductPrices?.CustomerTransactionInMerchantCurrency;
    const qty = toQty(line.Quantity);

    // ★번들 구성품은 건너뛴다 — 부모 번들 SKU 만 items 로. `send-order.ts:424` 와 같은 규칙이고,
    // 그쪽 주석이 *"child 는 GE 가 표시용으로 분해한 것일 뿐"* 이라 적고 있다. 안 거르면 Meta
    // `content_ids`/`contents` 가 부모+자식으로 **중복 계수**된다(`value` 는 주문 레벨이라 무관).
    // 표기가 서버와 다르다 — 클라는 `ParentCartItemID`. 둘 다 읽는다(위 타입 주석).
    //
    // ★**SKU 검사보다 앞이다.** child 는 어차피 버릴 라인이라, 뒤에 두면 SKU 가 빈 child 가
    // `without SKU` 경고를 오발화한다. go-live 검증이 「경고 0건」을 요구하므로 같은 클래스다.
    if (line.ParentCartItemID || line.ParentCartItemId) continue;

    const sku = (line.SKU || "").trim();
    if (!sku) {
      warnings.push("product line without SKU → dropped");
      continue;
    }

    // ⚠️ **단가 vs 라인 총액** — `*ListPrice`/`*SalePrice`/`*DiscountedPrice` 는 **단가**,
    // `*TotalPrice` 는 **라인 총액**이다(GE 문서 "Total price of all units").
    //
    // ⚠️ **`*ListPrice` 가 0 으로 온다.** Fixed 국가에는 우리가 `OriginalListPrice: 0` 을 보내고
    // (`get-checkout-cart-info.ts:123-126`), GE 문서 할인 샘플도 `CustomerListPrice: 0 /
    // CustomerSalePrice: 335` 다. 폴백 없이 쓰면 **`price: 0` 이 조용히 나간다.**
    // 사슬은 형제 경로(`send-order.ts:252`)와 같은 모양으로 맞춘다.
    const listRaw = fixed
      ? num(prices?.CustomerListPrice)
      : num(prices?.CustomerListPriceInMerchantCurrency);
    const saleRaw = fixed
      ? num(prices?.CustomerSalePrice)
      : num(prices?.CustomerSalePriceInMerchantCurrency);
    const discRaw = fixed
      ? num(prices?.CustomerDiscountedPrice)
      : num(prices?.CustomerDiscountedPriceInMerchantCurrency);
    const lineTotal = fixed
      ? num(prices?.CustomerTotalPrice)
      : num(prices?.CustomerTotalPriceInMerchantCurrency);

    const listUnit = listRaw || saleRaw || discRaw;
    const netUnit = discRaw || saleRaw || listUnit;
    if (listUnit <= 0) {
      warnings.push(`no positive price for sku=${sku} (list=0)`);
    }

    // ★**검산 기준은 `netUnit` 이다.** GE 는 `*TotalPrice` 를 **순가 × 수량**으로 만든다
    // (문서 할인 샘플: List 0 / Sale 335 / Total 1005 = 335×3). 정가로 검산하면 **할인 라인마다
    // 오발화**하고, 그러면 진짜 그룹 혼동이 났을 때 구별되지 않는다 — 상시 켜진 경고는 가드가
    // 아니다. 오차 허용은 반올림 몫만(수량 비례).
    if (lineTotal > 0) {
      const tolerance = 0.005 * qty + 0.01;
      if (Math.abs(netUnit * qty - lineTotal) > tolerance) {
        warnings.push(
          `line total mismatch sku=${sku}: net ${netUnit} × ${qty} = ${round2(netUnit * qty)} vs GE ${lineTotal} (${currency})`,
        );
      }
    }

    items.push({
      sku,
      slug: (line.ProductGroupCode || "").trim(),
      name: (line.ProductName || "").trim() || sku,
      qty,
      // ITEM_PRICE_CONVENTION: price=정가(단가) · discount=할인액(단가).
      price: listUnit,
      displayPrice: listUnit,
      discount: Math.max(0, round2(listUnit - netUnit)),
      options: "",
      thumbnail: "",
    });
  }

  if (items.length === 0) {
    return {
      payload: null,
      warnings: [...warnings, "no mappable product lines — not fired"],
    };
  }

  const subtotalAmount = round2(
    num(
      fixed
        ? orderPrices?.CustomerTotalProductsPrice
        : orderPrices?.CustomerTotalProductsPriceInMerchantCurrency,
    ),
  );
  const shippingPrice = round2(
    num(
      fixed
        ? orderPrices?.CustomerShippingPrice
        : orderPrices?.CustomerShippingPriceInMerchantCurrency,
    ),
  );

  return {
    payload: {
      orderId,
      items,
      currency,
      subtotalAmount,
      shippingPrice,
      shippingCountry: (details.CountryCode || "").trim().toUpperCase(),
    },
    warnings,
  };
}

/**
 * GE 통화는 우리 `CurrencyIsoCode`(5종) 밖일 수 있다(GE 는 100+ 통화). 두 sink 모두 이 값을
 * **라벨로만** 쓰고 가격 조회 축이 아니므로 런타임은 안전하다. enum 으로 좁히면 실청구 통화가
 * 아니라 우리 enum 에 맞춘 거짓 라벨이 나간다(`docs/analytics-funnel-policy.md` §1.1).
 */
export const asCurrencyLabel = (v: string) => v as CurrencyIsoCode;
